import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { useWeekStartsOn } from '@/lib/dates/useWeekStartsOn'
import {
  luxonLeadingEmptyDays,
  weekdayLetters,
} from '@/lib/dates/weekStart'
import { ClientRoadmapLoadingState } from '@/components/client/ClientTabLoadingStates'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { ChevronLeft, ChevronRight, ClipboardList, Plus } from 'lucide-react'
import PhaseRow, { WeekStat } from '@/components/client/roadmap/PhaseRow'
import AddPhaseDialog from '@/components/client/roadmap/AddPhaseDialog'
import DeleteAlert from '@/components/DeleteAlert'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import {
  patchRoadmap,
  removePhaseFromRoadmap,
  type RoadmapPhase,
} from '@/components/client/roadmap/roadmapCache'

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/roadmap',
)({
  component: ClientRoadmap,
  pendingComponent: ClientRoadmapLoadingState,
})

const WEIGHT_METRIC_ID = '11d92087-b60f-4c21-b685-1021662da260'
const STEPS_METRIC_ID = '03a40121-31c4-42da-b259-a60781dcb32e'

const average = (values: number[]) => {
  if (values.length === 0) {
    return null
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

const getPhaseWeeks = (phase: RoadmapPhase) => {
  const start = DateTime.fromISO(phase.start_date).startOf('day')
  const end = DateTime.fromISO(phase.end_date).endOf('day')
  const diffDays = Math.max(1, Math.floor(end.diff(start, 'days').days) + 1)
  const weekCount = Math.max(1, Math.ceil(diffDays / 7))

  return Array.from({ length: weekCount }, (_, index) => {
    const weekStart = start.plus({ days: index * 7 }).startOf('day')
    const weekEnd = DateTime.min(end, weekStart.plus({ days: 6 }).endOf('day'))

    return {
      weekNumber: index + 1,
      weekStart,
      weekEnd,
    }
  })
}

const getDaysInRange = (start: DateTime, end: DateTime) => {
  return Math.max(
    1,
    Math.floor(end.startOf('day').diff(start.startOf('day'), 'days').days) + 1,
  )
}

function ClientRoadmap() {
  const { clientId } = Route.useParams()
  const weekStartsOn = useWeekStartsOn()
  const utils = trpc.useUtils()
  const [activeTab, setActiveTab] = useState('phases')
  const [addPhaseOpen, setAddPhaseOpen] = useState(false)
  const [editingPhase, setEditingPhase] = useState<RoadmapPhase | null>(null)
  const [phaseToDelete, setPhaseToDelete] = useState<RoadmapPhase | null>(null)
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>(
    {},
  )

  const { data, isLoading } = trpc.roadmap.list.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )
  const deletePhase = trpc.roadmap.deletePhase.useMutation()

  const phaseRows = useMemo(() => data?.phases ?? [], [data?.phases])
  const weightUnitLabel = data?.weight_unit === 'lbs' ? 'Lbs' : 'Kg'
  const nextInactivePhaseId = useMemo(() => {
    return (
      phaseRows.find(
        (phase) => phase.status !== 'active' && phase.status !== 'completed',
      )?.id ?? null
    )
  }, [phaseRows])
  const activePhase = useMemo(
    () => phaseRows.find((phase) => phase.status === 'active') ?? null,
    [phaseRows],
  )
  const previousPhaseEndDate = useMemo(() => {
    if (editingPhase) {
      const index = phaseRows.findIndex((phase) => phase.id === editingPhase.id)
      if (index > 0) {
        return phaseRows[index - 1].end_date
      }
      return null
    }

    return phaseRows.length > 0
      ? phaseRows[phaseRows.length - 1].end_date
      : null
  }, [editingPhase, phaseRows])
  const hasPreviousPhaseForDialog = useMemo(() => {
    if (editingPhase) {
      const index = phaseRows.findIndex((phase) => phase.id === editingPhase.id)
      return index > 0
    }
    return phaseRows.length > 0
  }, [editingPhase, phaseRows])

  const dateRange = useMemo(() => {
    if (phaseRows.length === 0) {
      return null
    }

    const startDate = phaseRows.reduce(
      (min, phase) => (phase.start_date < min ? phase.start_date : min),
      phaseRows[0].start_date,
    )
    const endDate = phaseRows.reduce(
      (max, phase) => (phase.end_date > max ? phase.end_date : max),
      phaseRows[0].end_date,
    )

    return {
      start: DateTime.fromISO(startDate).startOf('day'),
      end: DateTime.fromISO(endDate).endOf('day'),
    }
  }, [phaseRows])

  const currentYear = DateTime.now().year
  const availableYears = useMemo(() => {
    const maxYearFromToday = currentYear + 2
    if (!dateRange) {
      return [currentYear, currentYear + 1, maxYearFromToday]
    }

    const startYear = dateRange.start.year
    const endYear = Math.max(dateRange.end.year, maxYearFromToday)
    const years = []
    for (let year = startYear; year <= endYear; year += 1) {
      years.push(year)
    }
    return years.length > 0
      ? years
      : [currentYear, currentYear + 1, maxYearFromToday]
  }, [currentYear, dateRange])
  const [selectedYear, setSelectedYear] = useState(currentYear)

  useEffect(() => {
    if (!availableYears.includes(selectedYear)) {
      setSelectedYear(
        availableYears.includes(currentYear) ? currentYear : availableYears[0],
      )
    }
  }, [availableYears, currentYear, selectedYear])

  useEffect(() => {
    if (!activePhase) {
      return
    }
    setExpandedPhases((prev) => {
      if (Object.keys(prev).length > 0) {
        return prev
      }
      return { [activePhase.id]: true }
    })
  }, [activePhase])

  const milestonesByPhase = useMemo(() => {
    return (data?.milestones ?? []).reduce<
      Record<string, NonNullable<typeof data>['milestones']>
    >((acc, milestone) => {
      const list = acc[milestone.phase_id] ?? []
      list.push(milestone)
      acc[milestone.phase_id] = list
      return acc
    }, {})
  }, [data?.milestones])

  const handleDeletePhase = async () => {
    if (!phaseToDelete) {
      return
    }

    try {
      await deletePhase.mutateAsync({
        clientId,
        id: phaseToDelete.id,
      })
      patchRoadmap(utils, clientId, (old) =>
        removePhaseFromRoadmap(old, phaseToDelete.id),
      )
      setPhaseToDelete(null)
    } catch (error) {
      console.error('Error deleting phase', error)
      errorToast(error instanceof Error ? error : null, 'Failed to delete phase')
    }
  }

  const weeklyStatsByPhase = useMemo(() => {
    const metrics = data?.metric_logs ?? []
    const foods = data?.food_logs ?? []
    const cardio = data?.cardio_logs ?? []
    const target = data?.steps_daily_target ?? null

    return phaseRows.reduce<Record<string, WeekStat[]>>((acc, phase) => {
      const phaseWeeks = getPhaseWeeks(phase).map((week) => {
        const weekMetrics = metrics.filter((log) => {
          const loggedAt = DateTime.fromISO(log.logged_at)
          return loggedAt >= week.weekStart && loggedAt <= week.weekEnd
        })
        const weekFoods = foods.filter((log) => {
          const loggedAt = DateTime.fromISO(log.date)
          return loggedAt >= week.weekStart && loggedAt <= week.weekEnd
        })
        const weekCardio = cardio.filter((log) => {
          const loggedAt = DateTime.fromISO(log.logged_at)
          return loggedAt >= week.weekStart && loggedAt <= week.weekEnd
        })

        const weightByDay = new Map<string, number[]>()
        const stepsByDay = new Map<string, number[]>()
        weekMetrics.forEach((log) => {
          const dayKey = DateTime.fromISO(log.logged_at).toISODate()
          if (!dayKey) {
            return
          }
          if (log.metric_id === WEIGHT_METRIC_ID) {
            const values = weightByDay.get(dayKey) ?? []
            values.push(log.value)
            weightByDay.set(dayKey, values)
          }
          if (log.metric_id === STEPS_METRIC_ID) {
            const values = stepsByDay.get(dayKey) ?? []
            values.push(log.value)
            stepsByDay.set(dayKey, values)
          }
        })

        const weightDailyAverages = Array.from(weightByDay.values())
          .map((values) => average(values))
          .filter((value): value is number => value !== null)
        const stepsDailyAverages = Array.from(stepsByDay.values())
          .map((values) => average(values))
          .filter((value): value is number => value !== null)

        const caloriesByDay = new Map<string, number>()
        weekFoods.forEach((log) => {
          const calories = log.calories ?? null
          if (calories === null) {
            return
          }
          const dayKey = DateTime.fromISO(log.date).toISODate()
          if (!dayKey) {
            return
          }
          const total = calories * log.serving_scale * (log.serving_size ?? 1)
          caloriesByDay.set(dayKey, (caloriesByDay.get(dayKey) ?? 0) + total)
        })
        const dailyCalories = Array.from(caloriesByDay.values())

        const cardioByDay = new Map<string, number>()
        weekCardio.forEach((log) => {
          const dayKey = DateTime.fromISO(log.logged_at).toISODate()
          if (!dayKey) {
            return
          }
          const minutes = log.duration / 60
          cardioByDay.set(dayKey, (cardioByDay.get(dayKey) ?? 0) + minutes)
        })
        const dailyCardio = Array.from(cardioByDay.values())

        const loggedDays = new Set<string>([
          ...Array.from(weightByDay.keys()),
          ...Array.from(stepsByDay.keys()),
          ...Array.from(caloriesByDay.keys()),
          ...Array.from(cardioByDay.keys()),
        ])

        const daysInWeek = getDaysInRange(week.weekStart, week.weekEnd)
        const performance =
          loggedDays.size > 0
            ? `${Math.round((loggedDays.size / daysInWeek) * 100)}%`
            : null

        return {
          weekNumber: week.weekNumber,
          dateLabel: week.weekStart.toFormat('dd/MM/yyyy'),
          avgBodyweight: average(weightDailyAverages),
          avgCalories: average(dailyCalories),
          avgSteps: average(stepsDailyAverages),
          stepTarget: target,
          avgCardio: average(dailyCardio),
          performance,
        }
      })

      acc[phase.id] = phaseWeeks
      return acc
    }, {})
  }, [phaseRows, data?.metric_logs, data?.food_logs, data?.cardio_logs, data?.steps_daily_target])

  if (isLoading) {
    return <ClientRoadmapLoadingState />
  }

  const today = DateTime.now().startOf('day')

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <div className="w-full md:w-[90%] mx-auto px-4 lg:px-0 mb-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <TabsList>
            <TabsTrigger value="phases">Phases</TabsTrigger>
            <TabsTrigger value="roadmap">Roadmap</TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            onClick={() => {
              setEditingPhase(null)
              setAddPhaseOpen(true)
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add phase
          </Button>
        </div>

        <AddPhaseDialog
          clientId={clientId}
          previousPhaseEndDate={previousPhaseEndDate}
          hasPreviousPhase={hasPreviousPhaseForDialog}
          phase={editingPhase}
          open={addPhaseOpen}
          onOpenChange={setAddPhaseOpen}
        />
        <DeleteAlert
          open={phaseToDelete !== null}
          title="Delete phase?"
          description="This action cannot be undone."
          onCancel={() => setPhaseToDelete(null)}
          onDelete={handleDeletePhase}
        />

        <TabsContent value="phases" className="mt-0">
          {phaseRows.length === 0 ? (
            <Empty className="py-12">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ClipboardList className="size-6" />
                </EmptyMedia>
                <EmptyTitle>No phases</EmptyTitle>
                <EmptyDescription>
                  There are no phases for this client yet.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button
                  onClick={() => {
                    setEditingPhase(null)
                    setAddPhaseOpen(true)
                  }}
                >
                  Add phase
                </Button>
              </EmptyContent>
            </Empty>
          ) : (
            <div className="space-y-4">
              {phaseRows.map((phase, index) => {
                const previousPhase = index > 0 ? phaseRows[index - 1] : null
                return (
                  <PhaseRow
                    key={phase.id}
                    clientId={clientId}
                    phase={phase}
                    weeks={weeklyStatsByPhase[phase.id] ?? []}
                    milestones={milestonesByPhase[phase.id] ?? []}
                    isExpanded={expandedPhases[phase.id] ?? false}
                    canActivate={phase.id === nextInactivePhaseId}
                    previousPhaseStartDate={previousPhase?.start_date ?? null}
                    weightUnitLabel={weightUnitLabel}
                    onToggle={() =>
                      setExpandedPhases((prev) => ({
                        ...prev,
                        [phase.id]: !(prev[phase.id] ?? false),
                      }))
                    }
                    onEditPhase={(selectedPhase) => {
                      setEditingPhase(selectedPhase)
                      setAddPhaseOpen(true)
                    }}
                    onDeletePhase={(selectedPhase) =>
                      setPhaseToDelete(selectedPhase)
                    }
                  />
                )
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="roadmap" className="mt-0">
          {phaseRows.length === 0 ? (
            <Empty className="py-12">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ClipboardList className="size-6" />
                </EmptyMedia>
                <EmptyTitle>No roadmap yet</EmptyTitle>
                <EmptyDescription>
                  Add phases to see a yearly roadmap view.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button
                  onClick={() => {
                    setEditingPhase(null)
                    setAddPhaseOpen(true)
                  }}
                >
                  Add phase
                </Button>
              </EmptyContent>
            </Empty>
          ) : (
            <Card className="p-6 md:p-8">
              <div className="flex items-center justify-center gap-4 mb-8">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setSelectedYear((prev) =>
                      Math.max(availableYears[0], prev - 1),
                    )
                  }
                  disabled={selectedYear <= availableYears[0]}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="px-6 py-2 border rounded-md text-sm font-semibold">
                  {selectedYear}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setSelectedYear((prev) =>
                      Math.min(
                        availableYears[availableYears.length - 1],
                        prev + 1,
                      ),
                    )
                  }
                  disabled={
                    selectedYear >= availableYears[availableYears.length - 1]
                  }
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8">
                {Array.from({ length: 12 }, (_, monthIndex) => {
                  const month = monthIndex + 1
                  const monthStart = DateTime.local(
                    selectedYear,
                    month,
                    1,
                  ).startOf('day')
                  const monthEnd = monthStart.endOf('month')
                  const phasesInMonth = phaseRows.filter((phase) => {
                    const start = DateTime.fromISO(phase.start_date)
                    const end = DateTime.fromISO(phase.end_date)
                    return start <= monthEnd && end >= monthStart
                  })
                  const activePhaseRanges = phasesInMonth
                    .filter((phase) => phase.status === 'active')
                    .map((phase) => ({
                      start: DateTime.fromISO(phase.start_date).startOf('day'),
                      end: DateTime.fromISO(phase.end_date).endOf('day'),
                    }))
                  const inactivePhaseRanges = phasesInMonth
                    .filter((phase) => phase.status !== 'active')
                    .map((phase) => ({
                      start: DateTime.fromISO(phase.start_date).startOf('day'),
                      end: DateTime.fromISO(phase.end_date).endOf('day'),
                      startKey: DateTime.fromISO(phase.start_date).toISODate(),
                      endKey: DateTime.fromISO(phase.end_date).toISODate(),
                    }))
                  const monthName = monthStart.toFormat('LLLL')
                  const daysInMonth = monthStart.daysInMonth ?? 0
                  const leadingEmpty = luxonLeadingEmptyDays(
                    monthStart.weekday,
                    weekStartsOn,
                  )
                  const totalSlots = leadingEmpty + daysInMonth
                  const daySlots = Array.from(
                    { length: totalSlots },
                    (_, index) =>
                      index < leadingEmpty ? null : index - leadingEmpty + 1,
                  )
                  const dayClasses = (day: number | null) => {
                    if (!day) {
                      return 'bg-transparent'
                    }
                    const date = monthStart.set({ day }).startOf('day')
                    const dateKey = date.toISODate()
                    const isActiveDay = activePhaseRanges.some(
                      (range) => date >= range.start && date <= range.end,
                    )
                    if (isActiveDay) {
                      return 'bg-emerald-100 text-emerald-900'
                    }
                    const isInactiveDay = inactivePhaseRanges.some(
                      (range) => date >= range.start && date <= range.end,
                    )
                    const isInactiveStart = inactivePhaseRanges.some(
                      (range) => range.startKey === dateKey,
                    )
                    const isInactiveEnd = inactivePhaseRanges.some(
                      (range) => range.endKey === dateKey,
                    )
                    if (isInactiveDay) {
                      if (isInactiveStart || isInactiveEnd) {
                        return 'bg-muted/60 text-muted-foreground ring-1 ring-muted-foreground/60'
                      }
                      return 'bg-muted/60 text-muted-foreground'
                    }
                    return 'text-muted-foreground'
                  }
                  const dayTooltip = (day: number | null) => {
                    if (!day) {
                      return null
                    }
                    const dateKey = monthStart.set({ day }).toISODate()
                    if (!dateKey) {
                      return null
                    }
                    const labels = phasesInMonth.flatMap((phase) => {
                      const startKey = DateTime.fromISO(
                        phase.start_date,
                      ).toISODate()
                      const endKey = DateTime.fromISO(
                        phase.end_date,
                      ).toISODate()
                      const entries: string[] = []
                      if (startKey === dateKey) {
                        entries.push(`${phase.title} starts`)
                      }
                      if (endKey === dateKey) {
                        entries.push(`${phase.title} ends`)
                      }
                      return entries
                    })
                    return labels.length > 0 ? labels.join('\n') : null
                  }
                  const phaseBadgeClasses = (phase: RoadmapPhase) =>
                    phase.status === 'active'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'bg-muted text-muted-foreground'

                  return (
                    <div
                      key={`${selectedYear}-${month}`}
                      className="space-y-3 select-none"
                    >
                      <div className="text-sm font-semibold">{monthName}</div>
                      <div className="grid grid-cols-7 text-[11px] text-muted-foreground">
                        {weekdayLetters(weekStartsOn).map(
                          (label, index) => (
                            <div
                              key={`${monthName}-${label}-${index}`}
                              className="text-center"
                            >
                              {label}
                            </div>
                          ),
                        )}
                      </div>
                      <TooltipProvider delayDuration={150}>
                        <div className="grid grid-cols-7 gap-1 text-xs">
                          {daySlots.map((day, index) => (
                            <div
                              key={`${monthName}-day-${index}`}
                              className="flex items-center justify-center"
                            >
                              {day ? (
                                (() => {
                                  const tooltip = dayTooltip(day)
                                  const isToday =
                                    selectedYear === today.year &&
                                    month === today.month &&
                                    day === today.day
                                  const todayClass = isToday
                                    ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                                    : ''
                                  const dayCell = (
                                    <div
                                      className={`h-6 w-6 rounded-md flex items-center justify-center ${dayClasses(
                                        day,
                                      )} ${todayClass}`}
                                    >
                                      {day}
                                    </div>
                                  )
                                  return tooltip ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        {dayCell}
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <div className="whitespace-pre-line text-xs">
                                          {tooltip}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    dayCell
                                  )
                                })()
                              ) : (
                                <div className="h-6 w-6" />
                              )}
                            </div>
                          ))}
                        </div>
                      </TooltipProvider>
                      {phasesInMonth.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {phasesInMonth.map((phase) => (
                            <button
                              type="button"
                              key={`${phase.id}-${month}`}
                              className={`px-3 py-1 rounded-full text-xs text-center ${phaseBadgeClasses(
                                phase,
                              )} cursor-pointer hover:opacity-80`}
                              onClick={() => {
                                setEditingPhase(phase)
                                setAddPhaseOpen(true)
                              }}
                            >
                              {phase.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </Card>
          )}
        </TabsContent>
      </div>
    </Tabs>
  )
}
